import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Loader2, CheckCircle, Sparkles, Shield, Zap, ArrowRight, RotateCcw, Users, Briefcase, Target, UserCheck, Phone, Mail, MapPin, ChevronDown, ChevronUp, Globe, Search, Bot, Bell, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper: Format number with Indian comma grouping
const formatIndianNumber = (num) => {
  if (!num && num !== 0) return '0';
  return num.toLocaleString('en-IN');
};

// Section IDs for scroll navigation
const SECTIONS = ['hero', 'how-it-works', 'ai-power', 'stats', 'companies', 'cta'];

// Count-up animation hook
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView || hasStarted) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [startOnView, hasStarted]);

  useEffect(() => {
    if (!hasStarted || !end) return;
    
    const startTime = Date.now();
    const startValue = 0;
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.floor(startValue + (end - startValue) * easeOut);
      setCount(current);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    requestAnimationFrame(animate);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

export default function PortalHomePage() {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastFile, setLastFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [pageSettings, setPageSettings] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const uploadBoxRef = useRef(null);
  const sectionRefs = useRef({});

  // Smooth scroll animation with custom duration
  const smoothScrollTo = (targetPosition, duration = 1200) => {
    const startPosition = window.scrollY;
    const distance = targetPosition - startPosition;
    let startTime = null;

    const easeInOutCubic = (t) => {
      return t < 0.5 
        ? 4 * t * t * t 
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);
      
      window.scrollTo(0, startPosition + distance * easedProgress);
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  // Scroll to next/previous section based on current position
  const handleScrollButton = () => {
    if (isAtBottom) {
      // At bottom - scroll back to top
      smoothScrollTo(0, 1500);
    } else {
      // Find next section and scroll to it
      const nextIndex = Math.min(currentSection + 1, SECTIONS.length - 1);
      const nextSection = document.getElementById(SECTIONS[nextIndex]);
      if (nextSection) {
        const targetPosition = nextSection.getBoundingClientRect().top + window.scrollY;
        smoothScrollTo(targetPosition, 1200); // 1.2 seconds per section
      }
    }
  };

  // Track scroll position - check if at bottom
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2;
      SECTIONS.forEach((sectionId, index) => {
        const section = document.getElementById(sectionId);
        if (section) {
          const { top, bottom } = section.getBoundingClientRect();
          const sectionTop = top + window.scrollY;
          const sectionBottom = bottom + window.scrollY;
          if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
            setCurrentSection(index);
          }
        }
      });
      
      // Check if at bottom of page (within 100px threshold)
      const scrollTop = window.scrollY;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = window.innerHeight;
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 100);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load page settings and public stats
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/settings`);
        if (res.ok) {
          const data = await res.json();
          setPageSettings(data);
          // Apply title and meta
          if (data.home_page_title) {
            document.title = data.home_page_title;
          }
          if (data.favicon_url) {
            const favicon = document.querySelector("link[rel*='icon']") || document.createElement('link');
            favicon.type = 'image/x-icon';
            favicon.rel = 'shortcut icon';
            favicon.href = data.favicon_url;
            document.head.appendChild(favicon);
          }
          // Apply meta description
          if (data.home_page_description) {
            let metaDesc = document.querySelector('meta[name="description"]');
            if (!metaDesc) {
              metaDesc = document.createElement('meta');
              metaDesc.name = 'description';
              document.head.appendChild(metaDesc);
            }
            metaDesc.content = data.home_page_description;
          }
        }
      } catch (e) {
        console.error('Failed to load settings:', e);
      }
    };

    const loadPublicStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/stats`);
        if (res.ok) {
          const data = await res.json();
          setPublicStats(data);
          // Cache in localStorage for fallback
          localStorage.setItem('publicStats', JSON.stringify(data));
        }
      } catch (e) {
        console.error('Failed to load public stats:', e);
        // Try to use cached stats
        const cached = localStorage.getItem('publicStats');
        if (cached) {
          try {
            setPublicStats(JSON.parse(cached));
          } catch (err) {
            // Ignore parse errors for cached data
          }
        }
      }
    };

    loadSettings();
    loadPublicStats();

    // Refresh stats every 60 seconds
    const statsInterval = setInterval(loadPublicStats, 60000);
    return () => clearInterval(statsInterval);
  }, []);

  const scrollToUpload = () => {
    uploadBoxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  }, []);

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const uploadFile = async (file) => {
    const allowedTypes = ['.pdf', '.doc', '.docx', '.txt'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(ext)) {
      toast.error('Please upload a PDF, DOC, DOCX or TXT file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setLastFile(file);
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('source', 'portal');

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API_URL}/api/public/upload-resume`, {
        method: 'POST',
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error('Server error. Please try again.');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Upload failed');
      }

      // Check if duplicate - redirect to login instead of onboarding
      if (data.duplicate && data.redirect_to_login) {
        toast.info('Your resume is already on file! Please login to access your profile.');
        setTimeout(() => {
          // Redirect to portal login with phone pre-filled if available
          const loginUrl = data.phone 
            ? `/portal/login?phone=${encodeURIComponent(data.phone)}`
            : '/portal/login';
          navigate(loginUrl);
        }, 1500);
        return;
      }

      toast.success('Resume uploaded successfully!');
      
      // Track B (6 Sep 2026): keep the short-lived upload token the backend
      // issued for this candidate id; the onboarding page sends it back as
      // X-Upload-Token on every call. Without it those routes answer 401.
      try {
        if (data.upload_token) sessionStorage.setItem(`dcs_upload_token_${data.candidate_id}`, data.upload_token);
      } catch (e) { /* storage blocked - onboarding will ask for a re-upload */ }
      
      setTimeout(() => {
        navigate(`/onboarding/${data.candidate_id}`);
      }, 500);
      
    } catch (error) {
      setUploadError(error.message || 'Failed to upload resume');
      toast.error(error.message || 'Failed to upload resume');
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRetry = () => {
    if (lastFile) {
      setUploadError(null);
      uploadFile(lastFile);
    }
  };

  const handleManualEntry = () => {
    navigate('/onboarding/new?manual=true');
  };

  // Count-up hooks for stats
  const totalCandidatesCount = useCountUp(publicStats?.total_candidates || 0);
  const openJobsCount = useCountUp(publicStats?.open_jobs || 0);
  const totalOpeningsCount = useCountUp(publicStats?.total_openings || 0);
  const totalMatchesCount = useCountUp(publicStats?.total_matches || 0);
  const candidatesInJourneyCount = useCountUp(publicStats?.candidates_in_journey || 0);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Shining button and upload box animation styles */}
      <style>{`
        @keyframes shine {
          0% { left: -100%; }
          50%, 100% { left: 100%; }
        }
        .shine-button {
          position: relative;
          overflow: hidden;
        }
        .shine-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.6),
            transparent
          );
          animation: shine 2.5s infinite;
        }
        .shine-button:hover::before {
          animation: shine 1s infinite;
        }
        @keyframes pulse-gold {
          0%, 100% { box-shadow: 0 0 5px rgba(201, 165, 78, 0.4), 0 0 10px rgba(201, 165, 78, 0.2); }
          50% { box-shadow: 0 0 8px rgba(201, 165, 78, 0.6), 0 0 15px rgba(201, 165, 78, 0.3); }
        }
        .glow-gold {
          animation: pulse-gold 2s ease-in-out infinite;
        }
        @keyframes bounce-arrow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        .bounce-arrow {
          animation: bounce-arrow 1.5s ease-in-out infinite;
        }
        /* Upload box border shine animation */
        @keyframes border-shine {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .upload-box-shine {
          position: relative;
          padding: 4px;
          border-radius: 20px;
          background: linear-gradient(
            90deg, 
            #f0f0f0 0%,
            #f0f0f0 35%,
            #C9A54E 45%,
            #E8C96A 50%,
            #C9A54E 55%,
            #f0f0f0 65%,
            #f0f0f0 100%
          );
          background-size: 200% 100%;
          animation: border-shine 3s linear infinite;
        }
        .upload-box-shine > div {
          background: white;
          border-radius: 16px;
        }
      `}</style>

      {/* Gradient orb background for hero */}
      <div 
        className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(201, 165, 78, 0.12) 0%, rgba(201, 165, 78, 0.04) 40%, transparent 70%)',
        }}
      />

      {/* Header */}
      <header className="relative z-10">
        <div className="w-full px-8 lg:px-16 py-6 flex items-center justify-between max-md:flex-wrap max-md:justify-center max-md:gap-3 max-md:px-4 max-md:py-4">
          <div className="flex items-center gap-3">
            <img 
              src={pageSettings?.company_logo_url || "/images/daruwallas-logo.png"}
              alt={pageSettings?.company_name || "Daruwallas"}
              className="h-14 lg:h-16 w-auto max-md:h-11 max-md:max-w-[80vw] max-md:object-contain"
              onError={(e) => { e.target.src = "/images/daruwallas-logo.png"; }}
            />
          </div>
          <div className="flex items-center gap-4 max-md:w-full max-md:justify-center max-md:gap-2">
            {/* Employee Login - Simple button */}
            <button 
              onClick={() => navigate('/my')}
              className="px-5 py-2.5 text-xs font-semibold tracking-wider text-gray-700 bg-white border-2 border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all uppercase max-md:flex-1 max-md:min-h-[44px] max-md:px-3"
              data-testid="employee-login-btn"
            >
              EMPLOYEE LOGIN
            </button>
            
            {/* Employer Sign Up - Shining golden button with lighter background */}
            <button 
              onClick={() => navigate('/hr-signup')}
              className="shine-button glow-gold px-6 py-2.5 text-xs font-bold tracking-wider text-gray-800 rounded-lg transition-all hover:scale-105 uppercase max-md:flex-1 max-md:min-h-[44px] max-md:px-3"
              style={{ 
                background: 'linear-gradient(135deg, #F5E6B8 0%, #E8D48A 50%, #F5E6B8 100%)',
                border: '2px solid rgba(201, 165, 78, 0.5)'
              }}
              data-testid="employer-signup-btn"
            >
              EMPLOYER SIGN UP
            </button>
          </div>
        </div>
      </header>

      {/* ============ HERO SECTION ============ */}
      <section id="hero" className="relative z-10 min-h-[90vh] flex flex-col">
        <main className="flex-1 flex flex-col items-center px-8 lg:px-16 pb-8 pt-8 max-md:px-4">
          {/* Hero Text */}
          <div className="text-center mb-12 w-full max-w-4xl max-md:mb-8">
            <p 
              className="text-sm lg:text-base font-semibold tracking-[0.25em] uppercase mb-8 max-md:mb-5 max-md:tracking-[0.18em]"
              style={{ color: '#C9A54E' }}
            >
              THE FASTEST WAY TO GET HIRED
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-gray-900 mb-8 leading-[1.05] max-sm:text-[2rem] max-sm:mb-5">
              Drop your resume.<br />
              We handle the rest.
            </h1>
            <p className="text-gray-400 text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed max-sm:text-base">
              We carefully review your resume, build your profile, and connect you with opportunities that match your skills.
            </p>
          </div>

          {/* Upload Zone with shine sweep border */}
          <div className="w-full max-w-2xl mb-6" ref={uploadBoxRef}>
            <div className="upload-box-shine">
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => !uploading && document.getElementById('file-input').click()}
                className={`
                  relative rounded-2xl p-10 lg:p-14 max-md:p-6 text-center transition-all duration-200 cursor-pointer
                  border-2 border-dashed bg-white
                  ${isDragging 
                    ? 'border-[#C9A54E] bg-[#C9A54E]/5' 
                    : 'border-gray-200 hover:border-gray-300'
                  }
                  ${uploading ? 'pointer-events-none' : ''}
                `}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {uploading ? (
                  <div className="space-y-6">
                    <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-gray-50">
                      {uploadProgress < 100 ? (
                        <Loader2 className="w-10 h-10 animate-spin text-[#C9A54E]" />
                      ) : (
                        <CheckCircle className="w-10 h-10 text-[#C9A54E]" />
                      )}
                    </div>
                    <div>
                      <p className="text-xl font-medium text-gray-900">
                        {uploadProgress < 100 ? 'Uploading...' : 'Upload Complete!'}
                      </p>
                    </div>
                    <div className="w-56 mx-auto h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all duration-300 bg-[#C9A54E]"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-gray-50 mb-6">
                      <Upload className="w-9 h-9 text-gray-400" />
                    </div>
                    <h3 className="text-xl lg:text-2xl font-medium text-gray-900 mb-3">
                      Drag your resume here
                    </h3>
                    <p className="text-gray-400 text-lg mb-4">
                      or <span className="text-[#C9A54E] font-medium">click to browse</span>
                    </p>
                    <p className="text-base text-gray-300">
                      PDF, DOC, DOCX, TXT · Max 10MB
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

        {/* Upload Error with Retry */}
        {uploadError && lastFile && (
          <div className="w-full max-w-2xl mb-8 p-5 bg-red-50 border border-red-100 rounded-xl flex items-center gap-4" data-testid="upload-error-retry">
            <div className="flex-1">
              <p className="text-base font-medium text-red-700">Upload failed</p>
              <p className="text-sm text-red-500 mt-1">{lastFile.name} — {uploadError}</p>
            </div>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full text-base font-medium text-white transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #C9A54E 0%, #E8C96A 100%)' }}
              data-testid="retry-upload-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Retry
            </button>
          </div>
        )}

        {/* Manual Entry Link */}
        <div className="mb-14 max-md:mb-8">
          <button
            onClick={handleManualEntry}
            className="inline-flex items-center gap-2 text-base text-gray-400 hover:text-[#C9A54E] transition-colors max-md:min-h-[44px]"
          >
            No resume? Fill in manually
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-10 lg:gap-14 text-base text-gray-400 mb-12 max-md:gap-x-5 max-md:gap-y-3 max-md:mb-8">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-[#C9A54E]" />
            <span>Smart resume reading</span>
          </div>
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-[#C9A54E]" />
            <span>Your data is secure</span>
          </div>
          <div className="flex items-center gap-3">
            <Zap className="w-6 h-6 text-[#C9A54E]" />
            <span>Matched in seconds</span>
          </div>
        </div>
        </main>
      </section>

      {/* ============ SECTION 2: HOW IT WORKS ============ */}
      <section id="how-it-works" className="relative z-10 py-24 px-8 lg:px-16 bg-white max-md:py-14 max-md:px-4" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <p 
            className="text-sm font-semibold tracking-[0.25em] uppercase text-center mb-6"
            style={{ color: '#C9A54E' }}
          >
            HOW IT WORKS
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4">
            Three Simple Steps to Your Dream Job
          </h2>
          <p className="text-gray-500 text-center max-w-2xl mx-auto mb-16">
            We&apos;ve simplified the job search process so you can focus on what matters — preparing for interviews and landing offers.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-16 max-md:gap-10">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#C9A54E]/10 flex items-center justify-center">
                <Upload className="w-12 h-12 text-[#C9A54E]" />
              </div>
              <div className="text-xs font-semibold tracking-widest text-[#C9A54E] mb-3">STEP 1</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Upload your resume</h3>
              <p className="text-gray-500 leading-relaxed">
                Drop your resume above. Our AI reads it and builds your profile in seconds.
              </p>
            </div>
            
            {/* Step 2 */}
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#C9A54E]/10 flex items-center justify-center">
                <Target className="w-12 h-12 text-[#C9A54E]" />
              </div>
              <div className="text-xs font-semibold tracking-widest text-[#C9A54E] mb-3">STEP 2</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">We find jobs for you</h3>
              <p className="text-gray-500 leading-relaxed">
                Every day, we match new job openings to your skills. Log in any day to see jobs picked for you.
              </p>
            </div>
            
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#C9A54E]/10 flex items-center justify-center">
                <UserCheck className="w-12 h-12 text-[#C9A54E]" />
              </div>
              <div className="text-xs font-semibold tracking-widest text-[#C9A54E] mb-3">STEP 3</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">We apply for you</h3>
              <p className="text-gray-500 leading-relaxed">
                We automatically send your profile to companies that are hiring. You just wait for the interview call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 3: AI POWER ============ */}
      <section id="ai-power" className="relative z-10 py-24 px-8 lg:px-16 bg-[#FDFBF7] max-md:py-14 max-md:px-4">
        <div className="max-w-6xl mx-auto">
          <p 
            className="text-sm font-semibold tracking-[0.25em] uppercase text-center mb-6"
            style={{ color: '#C9A54E' }}
          >
            POWERED BY AI
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4">
            Our AI Works 24/7 to Find You Jobs
          </h2>
          <p className="text-gray-500 text-center max-w-2xl mx-auto mb-16">
            While you sleep, our intelligent system scans thousands of job portals, company websites, and recruitment platforms to find openings that match your profile.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-md:gap-4">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="w-14 h-14 mb-4 rounded-xl bg-gradient-to-br from-[#C9A54E]/20 to-[#E8C96A]/20 flex items-center justify-center">
                <Globe className="w-7 h-7 text-[#C9A54E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Searches Everywhere</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We scan Naukri, LinkedIn, Indeed, Monster, company career pages, and 100+ job boards every hour.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="w-14 h-14 mb-4 rounded-xl bg-gradient-to-br from-[#C9A54E]/20 to-[#E8C96A]/20 flex items-center justify-center">
                <Bot className="w-7 h-7 text-[#C9A54E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Matching</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Our AI understands your skills, experience, and preferences to find jobs that truly fit you — not just keyword matches.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="w-14 h-14 mb-4 rounded-xl bg-gradient-to-br from-[#C9A54E]/20 to-[#E8C96A]/20 flex items-center justify-center">
                <Bell className="w-7 h-7 text-[#C9A54E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant Alerts</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Get notified the moment a perfect opportunity is found. Be the first to apply before the crowd.
              </p>
            </div>
            
            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <div className="w-14 h-14 mb-4 rounded-xl bg-gradient-to-br from-[#C9A54E]/20 to-[#E8C96A]/20 flex items-center justify-center">
                <TrendingUp className="w-7 h-7 text-[#C9A54E]" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Auto Apply</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Enable one-click apply and we&apos;ll submit your profile to matching jobs automatically. No more filling forms.
              </p>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center max-md:mt-10 max-md:gap-6">
            <div>
              <p className="text-4xl font-bold text-[#C9A54E]">100+</p>
              <p className="text-sm text-gray-500 mt-1">Job Portals Scanned</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#C9A54E]">10,000+</p>
              <p className="text-sm text-gray-500 mt-1">Jobs Found Daily</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#C9A54E]">24/7</p>
              <p className="text-sm text-gray-500 mt-1">Always Searching</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#C9A54E]">2 min</p>
              <p className="text-sm text-gray-500 mt-1">Average Match Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 4: LIVE NUMBERS ============ */}
      <section id="stats" className="relative z-10 py-24 px-8 lg:px-16 bg-white max-md:py-14 max-md:px-4" data-testid="stats-section">
        <div className="max-w-6xl mx-auto">
          <p 
            className="text-sm font-semibold tracking-[0.25em] uppercase text-center mb-6"
            style={{ color: '#C9A54E' }}
          >
            OUR NUMBERS, LIVE
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4">
            Real Results, Real People
          </h2>
          <p className="text-gray-500 text-center max-w-2xl mx-auto mb-16">
            These numbers update in real-time. Every second, we&apos;re helping someone get closer to their dream job.
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8 max-md:gap-3">
            {/* Stat 1 */}
            <div 
              ref={totalCandidatesCount.ref}
              className="text-center p-6 rounded-2xl bg-[#FDFBF7] border border-[#C9A54E]/20"
            >
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {formatIndianNumber(totalCandidatesCount.count)}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                People who trusted us with their resume
              </p>
            </div>
            
            {/* Stat 2 */}
            <div 
              ref={openJobsCount.ref}
              className="text-center p-6 rounded-2xl bg-[#FDFBF7] border border-[#C9A54E]/20"
            >
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {formatIndianNumber(openJobsCount.count)}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                Jobs open right now
              </p>
            </div>
            
            {/* Stat 3 */}
            <div 
              ref={totalOpeningsCount.ref}
              className="text-center p-6 rounded-2xl bg-[#FDFBF7] border border-[#C9A54E]/20"
            >
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {formatIndianNumber(totalOpeningsCount.count)}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                Positions waiting to be filled
              </p>
            </div>
            
            {/* Stat 4 */}
            <div 
              ref={totalMatchesCount.ref}
              className="text-center p-6 rounded-2xl bg-[#FDFBF7] border border-[#C9A54E]/20"
            >
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {formatIndianNumber(totalMatchesCount.count)}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                Job matches made for our candidates
              </p>
              <p className="text-xs text-[#C9A54E] mt-2">
                Log in to see jobs matched to you
              </p>
            </div>
            
            {/* Stat 5 */}
            <div 
              ref={candidatesInJourneyCount.ref}
              className="text-center p-6 rounded-2xl bg-[#FDFBF7] border border-[#C9A54E]/20 col-span-2 md:col-span-1"
            >
              <p className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {formatIndianNumber(candidatesInJourneyCount.count)}
              </p>
              <p className="text-sm text-gray-500 leading-tight">
                People on their job journey with us today
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SECTION 5: COMPANIES THAT HIRE THROUGH US ============ */}
      {publicStats?.approved_clients?.length > 0 && (
        <section id="companies" className="relative z-10 py-24 bg-[#FDFBF7] overflow-hidden max-md:py-14" data-testid="companies-section">
          <div className="max-w-6xl mx-auto px-8 lg:px-16 max-md:px-4">
            <p 
              className="text-sm font-semibold tracking-[0.25em] uppercase text-center mb-6"
              style={{ color: '#C9A54E' }}
            >
              COMPANIES THAT HIRE THROUGH US
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-center text-gray-900 mb-4">
              Top Companies Trust Us
            </h2>
            <p className="text-gray-500 text-center max-w-2xl mx-auto mb-12">
              From IT giants to innovative startups, these companies rely on us to find their next great hire.
            </p>
          </div>
          
          {/* Marquee Container - 100% larger logos */}
          <div className="space-y-6">
            {/* Row 1 - Scrolling Left */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-40 bg-gradient-to-r from-[#FDFBF7] to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-[#FDFBF7] to-transparent z-10" />
              <div className="flex animate-marquee-left">
                {/* First set */}
                <div className="flex gap-6 pr-6">
                  {publicStats.approved_clients.slice(0, Math.ceil(publicStats.approved_clients.length / 2)).map((client, idx) => (
                    <div 
                      key={`left-1-${idx}`}
                      className="w-[280px] h-[140px] px-6 py-4 rounded-2xl bg-white flex items-center justify-center shadow-md border border-gray-100"
                    >
                      <img 
                        src={client.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`} 
                        alt={client.name}
                        className="max-h-[100px] max-w-[240px] object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`; }}
                      />
                    </div>
                  ))}
                </div>
                {/* Duplicate set for seamless loop */}
                <div className="flex gap-6 pr-6">
                  {publicStats.approved_clients.slice(0, Math.ceil(publicStats.approved_clients.length / 2)).map((client, idx) => (
                    <div 
                      key={`left-2-${idx}`}
                      className="w-[280px] h-[140px] px-6 py-4 rounded-2xl bg-white flex items-center justify-center shadow-md border border-gray-100"
                    >
                      <img 
                        src={client.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`} 
                        alt={client.name}
                        className="max-h-[100px] max-w-[240px] object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`; }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Row 2 - Scrolling Right */}
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-40 bg-gradient-to-r from-[#FDFBF7] to-transparent z-10" />
              <div className="absolute right-0 top-0 bottom-0 w-40 bg-gradient-to-l from-[#FDFBF7] to-transparent z-10" />
              <div className="flex animate-marquee-right">
                {/* First set */}
                <div className="flex gap-6 pr-6">
                  {publicStats.approved_clients.slice(Math.ceil(publicStats.approved_clients.length / 2)).map((client, idx) => (
                    <div 
                      key={`right-1-${idx}`}
                      className="w-[280px] h-[140px] px-6 py-4 rounded-2xl bg-white flex items-center justify-center shadow-md border border-gray-100"
                    >
                      <img 
                        src={client.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`} 
                        alt={client.name}
                        className="max-h-[100px] max-w-[240px] object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`; }}
                      />
                    </div>
                  ))}
                </div>
                {/* Duplicate set for seamless loop */}
                <div className="flex gap-6 pr-6">
                  {publicStats.approved_clients.slice(Math.ceil(publicStats.approved_clients.length / 2)).map((client, idx) => (
                    <div 
                      key={`right-2-${idx}`}
                      className="w-[280px] h-[140px] px-6 py-4 rounded-2xl bg-white flex items-center justify-center shadow-md border border-gray-100"
                    >
                      <img 
                        src={client.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`} 
                        alt={client.name}
                        className="max-h-[100px] max-w-[240px] object-contain"
                        loading="lazy"
                        onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(client.name)}&background=f5f5f5&color=333&size=200&font-size=0.28`; }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          {/* CSS for marquee animation */}
          <style>{`
            @keyframes marquee-left {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes marquee-right {
              0% { transform: translateX(-50%); }
              100% { transform: translateX(0); }
            }
            .animate-marquee-left {
              animation: marquee-left 30s linear infinite;
            }
            .animate-marquee-right {
              animation: marquee-right 30s linear infinite;
            }
            .animate-marquee-left:hover,
            .animate-marquee-right:hover {
              animation-play-state: paused;
            }
          `}</style>
        </section>
      )}

      {/* ============ SECTION 6: ONE-LINE EXPLAINER BAND ============ */}
      <section 
        id="cta"
        className="relative z-10 py-20 px-8 lg:px-16 max-md:py-14 max-md:px-4"
        style={{ background: 'linear-gradient(135deg, #C9A54E 0%, #E8C96A 100%)' }}
        data-testid="gold-cta-band"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Find Your Dream Job?
          </h2>
          <p className="text-xl lg:text-2xl font-medium text-gray-800 mb-10 leading-relaxed max-sm:text-lg max-md:mb-8">
            Upload your resume once. Log in daily to see new jobs that fit you. We send your profile to hiring companies — automatically.
          </p>
          <button
            onClick={scrollToUpload}
            className="inline-flex items-center gap-3 px-10 py-5 rounded-full text-xl font-bold bg-gray-900 text-white hover:bg-gray-800 transition-all hover:scale-105 shadow-xl max-md:w-full max-md:justify-center max-md:px-6 max-md:py-4 max-md:text-base"
            data-testid="upload-my-resume-btn"
          >
            Upload my resume now
            <ArrowRight className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="relative z-10 py-16 px-8 lg:px-16 bg-gray-900 text-white max-md:py-12 max-md:px-4" data-testid="footer">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-12 max-md:gap-8">
            {/* Logo and Company Info */}
            <div className="text-center md:text-left">
              <img 
                src={pageSettings?.company_logo_url || "/images/daruwallas-logo.png"}
                alt="Daruwallas"
                className="h-14 w-auto mx-auto md:mx-0 mb-4 brightness-0 invert max-md:h-11 max-md:max-w-[80vw] max-md:object-contain"
                onError={(e) => { e.target.src = "/images/daruwallas-logo.png"; }}
              />
              <p className="text-gray-400 text-sm max-w-xs">
                Daruwalla Consultancy Services LLP — Recruitment & Staffing, Pan India
              </p>
            </div>
            
            {/* Contact Info */}
            <div className="flex flex-col sm:flex-row gap-8 text-center md:text-left max-md:gap-6">
              <div>
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-300 mb-2">
                  <Mail className="w-4 h-4 text-[#C9A54E]" />
                  <a href="mailto:care@daruwallas.com" className="hover:text-[#C9A54E] transition-colors">
                    care@daruwallas.com
                  </a>
                </div>
                <div className="flex items-center justify-center md:justify-start gap-2 text-gray-300">
                  <Phone className="w-4 h-4 text-[#C9A54E]" />
                  <a href="tel:+919167896969" className="hover:text-[#C9A54E] transition-colors">
                    +91 9167896969
                  </a>
                </div>
              </div>
              
              <div>
                <div className="flex items-start justify-center md:justify-start gap-2 text-gray-300 mb-4">
                  <MapPin className="w-4 h-4 text-[#C9A54E] mt-0.5 flex-shrink-0" />
                  <span className="text-sm max-w-[200px]">
                    29 Hanuman Building, Behind Old RBI, Fort, Mumbai 400001
                  </span>
                </div>
                <button 
                  onClick={() => navigate('/my')}
                  className="text-[#C9A54E] hover:text-[#E8C96A] font-medium transition-colors max-md:inline-flex max-md:items-center max-md:min-h-[44px]"
                >
                  Login →
                </button>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* ============ FLOATING SCROLL BUTTON ============ */}
      <button
        onClick={handleScrollButton}
        className="fixed bottom-8 right-8 z-50 w-14 h-14 rounded-full bg-white shadow-lg border-2 border-[#C9A54E]/30 flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all duration-300 group max-md:right-4"
        style={{
          boxShadow: '0 4px 20px rgba(201, 165, 78, 0.25)',
          bottom: 'calc(2rem + env(safe-area-inset-bottom, 0px))'
        }}
        data-testid="floating-scroll-btn"
        aria-label={isAtBottom ? 'Scroll to top' : 'Scroll to bottom'}
      >
        {isAtBottom ? (
          <ChevronUp className="w-6 h-6 text-[#C9A54E] group-hover:text-[#B8943D] transition-colors" />
        ) : (
          <ChevronDown className="w-6 h-6 text-[#C9A54E] bounce-arrow group-hover:text-[#B8943D] transition-colors" />
        )}
      </button>
    </div>
  );
}
